import axios, { AxiosError, AxiosResponse } from "axios";
import React from "react";
import getConfig from "next/config";
import { triggerSnowplowImpressionEvent, triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { ApiArticle } from "utils/api";
import { getObserver } from "utils/observer";
import styles from "./RecentArticles.module.scss";

type IPrimaryTopic = Pick<ApiArticle, "title" | "slug">;

type IArticleTile = Pick<ApiArticle, "id" | "leadMediaPhoto" | "primaryTopic" | "title" | "uri" | "writer" | "companySponsor">;

// Base tracking props common across all tiles.
const trackingProps = {
    // SnowplowClickEventProps
    sp_click_button_type: null,
    sp_click_component: "tile",
    sp_click_component_tile_type: "article",
    sp_click_insert_type: null,
    sp_click_search_id: null,
    sp_click_target_page_section: "advice",
    // SnowplowTileContentProps
    sp_tile_entity_uuid: "",
    sp_tile_location: "recent_articles",
    sp_tile_type: "article",
    // SnowplowImpressionProps
    sp_impression_component: "tile",
    sp_impression_tile_type: "article",
    sp_impression_insert_type: null,
    sp_impression_modal_type: null,
    sp_impression_button_type: null,
    sp_impression_search_id: null,
    sp_impression_position: "bottom",
};

// Generated the tracking props for each tile.
const getTrackingProps = (article: IArticleTile, index: number) => {
    const { id, companySponsor } = article;
    const props = {
        ...trackingProps,
        // SnowplowClickEventProps
        sp_click_button_text: null,
        sp_click_link_text: article.title,
        sp_click_position: "bottom",
        // SnowplowTileContentProps
        sp_tile_company_id: companySponsor?.id?.toString() ?? "",
        sp_tile_entity_id: id.toString() ?? "",
        sp_tile_order: index,
    };
    return props;
};

// Generates an individual tile.
const ArticleTile = (props: { article: IArticleTile, index: number }) => {
    const { article, index } = props;
    const {
        leadMediaPhoto,
        title,
        uri,
        writer,
    } = article;
    const { writerName } = writer;
    const {
        publicRuntimeConfig: { staticAssetHost }
    } = getConfig();
    // url text could be null and lead media might not exist
    const url = leadMediaPhoto?.[0]?.url ?? `${staticAssetHost}/media/lead/placeholder.png`;
    const ref = React.useRef<HTMLElement>();

    const tileObserver = getObserver((element) => triggerSnowplowImpressionEvent(element));

    React.useEffect(() => {
        tileObserver.observe(ref.current);
        return () => {
            tileObserver.disconnect();
        };
    }, []);
    const tileTrackingProps = getTrackingProps(article, index);

    return (
        <article
            className={styles.RecentTile}
            ref={ref}
            data-tracking-props={JSON.stringify(tileTrackingProps)}
        >
            <div
                className={styles.linkWrapper}
            >
                <img
                    className={styles.image}
                    height="111"
                    width="111"
                    alt=""
                    loading="lazy"
                    src={`https://pilbox.themuse.com/image.jpg?filter=antialias&opt=1&mode=crop&pos=center&prog=1&q=keep&url=${url}&w=111&h=111`}
                />
                <div className={styles.content}>
                    <p className={styles.author}>{writerName}</p>
                    <p className={styles.title}>
                        <a
                            href={`/${uri}`}
                            onClick={(e) => triggerSnowplowClickEvent({ ...e, ...tileTrackingProps })}
                        >
                            {title}
                        </a>
                    </p>
                </div>
            </div>
        </article>
    );
};

// The main component with all tiles.
const RecentArticles = (props: { primaryTopic: IPrimaryTopic }) => {
    const { primaryTopic } = props;
    const [topic, setTopic] = React.useState(primaryTopic.slug);
    const [articles, setArticles] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [outerHeading, setOuterHeading] = React.useState(`Recent Articles in ${primaryTopic.title}`);
    const {
        publicRuntimeConfig: { staticAssetHost, appName, apiRequestTimeoutMs }
    } = getConfig();

    const fetchRecentArticles = async () => {
        setIsLoading(true);
        const url = `${staticAssetHost}/recent-articles/${topic}.json`;
        return axios.get(url, {
            method: "GET",
            timeout: apiRequestTimeoutMs,
            headers: {
                "content-type": "application/json;charset=UTF-8"
            }
        })
            .then((response: AxiosResponse) => {
                const { data } = response;
                // Filter out the current url and include a maximum of 6.
                const list = data?.data?.filter((x: ApiArticle) => !window.location.pathname.includes(x.uri)).slice(0, 6);
                // If we still have any articles lets render them.
                if (list.length) {
                    setArticles(list);
                    setIsLoading(false);
                    return true;
                }
                // Not enough data, but not really an error, let's warn people.
                console.warn(`${appName} received insufficient data from ${url}`);
                // Update the state so we can fetch the fallback data.
                setTopic("most_recent");
                setIsLoading(false);
                setOuterHeading("Recent Articles");
                return false;
            })
            .catch((error: AxiosError) => {
                if (topic === "most_recent") {
                    // Yikes! The fallback data is bad too? Let's be noisy.
                    console.error(error);
                    return false;
                }
                // Ok the first url is bad but we can setup for the fallback data here too.
                setTopic("most_recent");
                setOuterHeading("Recent Articles");
                setIsLoading(false);
                return true;
            });
    };

    React.useEffect(() => {
        fetchRecentArticles();
    }, [topic]);

    if (isLoading) {
        return (<aside data-msg="not ready">Loading ...</aside>);
    }

    if (!isLoading && !articles?.length) {
        return (<aside data-msg="no data" />);
    }

    return (
        <aside className={styles.RecentArticles}>
            <h2 className={styles.RecentArticlesHeading}>
                {outerHeading}
            </h2>
            {articles.map((article: IArticleTile, index: number) => (
                <ArticleTile
                    key={article.uri}
                    article={article}
                    index={index + 1}
                />
            ))}
        </aside>
    );
};

export { RecentArticles };
